import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr, downloadBlob } from "@/lib/api";
import { Icon, Button, IconButton, TextField, Badge, Panel, PageHead, EmptyState } from "@/sentinel";

const STATUS_ICON = { Proposed: "CircleDashed", Approved: "Check", Rejected: "X" };

/** Multi-line field in the TextField style (the library has no textarea). */
function TextArea({ label, rows = 3, ...rest }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} rows={rows} className="sn-input" style={{ padding: "var(--space-2)", lineHeight: "20px", resize: "vertical" }} {...rest} />
      </div>
    </div>
  );
}

function DThread({ did }) {
  const [text, setText] = useState("");
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-dcomments", did], queryFn: () => api.pmDecisionComments(did) });
  const send = async () => {
    if (!text.trim()) return;
    try { await api.pmAddDecisionComment(did, text); setText(""); refetch(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="sn-stack" style={{ gridColumn: 2, gap: "var(--space-2)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-hairline)" }} data-testid={`pm-dthread-${did}`}>
      {rows.map((c) => (
        <div key={c.id} className="sn-small"><span className="sn-faint">{c.author}: </span><span style={{ color: "var(--text-primary)" }}>{c.body}</span></div>
      ))}
      <div className="sn-row" style={{ flexWrap: "nowrap", gap: "var(--space-1)" }}>
        <TextField value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Discuss before deciding… (@mention supported)" aria-label="Comment on this decision" className="flex-1" data-testid="pm-dcomment-input" />
        <IconButton icon={Send} label="Send comment" size="sm" onClick={send} data-testid="pm-dcomment-send" />
      </div>
    </div>
  );
}

export default function Decisions() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", detail: "" });
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-decisions"], queryFn: api.pmDecisions });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["pm-summary"] }); };

  const propose = async () => {
    if (!form.title.trim()) { toast.error("Title required."); return; }
    try { await api.pmProposeDecision(form); setForm({ title: "", detail: "" }); setAdding(false); refresh(); toast.success("Decision proposed — awaiting approval."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const decide = async (id, status) => {
    try { await api.pmDecide(id, status); refresh(); toast.success(`Decision ${status.toLowerCase()}.`); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => { try { await api.pmDeleteDecision(id); refresh(); } catch (e) { toast.error(e.message); } };

  const pending = rows.filter((d) => d.status === "Proposed").length;

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-decisions">
      <PageHead
        crumb={["Project", "Decisions log"]}
        title="Decisions"
        context="Stakeholder decisions with an approval workflow."
        actions={
          <>
            <Button icon="Download" onClick={async () => downloadBlob(await api.pmExport("decisions"), "sentinel_decisions.csv")} data-testid="pm-export-decisions">CSV</Button>
            <Button variant="primary" icon="Plus" aria-expanded={adding} onClick={() => setAdding((v) => !v)} data-testid="pm-propose-btn">Propose decision</Button>
          </>
        }
      />

      {adding && (
        <Panel title="Propose a decision" icon="Gavel" data-testid="pm-propose-form">
          <div className="sn-stack">
            <TextField label="Title" placeholder="Decision title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pm-decision-title" />
            <TextArea label="Context" placeholder="Context / detail" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} rows={3} data-testid="pm-decision-detail" />
            <Button variant="primary" style={{ alignSelf: "flex-start" }} onClick={propose} data-testid="pm-decision-submit">Submit for approval</Button>
          </div>
        </Panel>
      )}

      {rows.length === 0 ? (
        <EmptyState icon="Gavel" title="No decisions yet">Propose one to start the approval workflow.</EmptyState>
      ) : (
        <Panel title="Decisions log" icon="Gavel" flush meta={<span className="sn-sans-num">{rows.length} total · {pending} awaiting approval</span>}>
          {rows.map((d) => (
            <article key={d.id} className="sn-dlog" data-testid={`pm-decision-${d.id}`}>
              <span className="sn-dlog-dot"><Icon name="Gavel" size={14} /></span>
              <div className="sn-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="sn-stack" style={{ gap: "var(--space-1)", minWidth: 0, flex: 1 }}>
                  <h3 className="sn-dlog-t">{d.title}</h3>
                  <div className="sn-dlog-meta">
                    <Badge icon={STATUS_ICON[d.status]} data-testid="pm-decision-status">{d.status.toUpperCase()}</Badge>
                    <span>Proposed by {d.proposed_by}{d.decided_by ? ` · ${d.status.toLowerCase()} by ${d.decided_by}` : ""}</span>
                    {d.created_at && <><span>·</span><span className="sn-num">{String(d.created_at).slice(0, 10)}</span></>}
                  </div>
                </div>
                {canEdit && (
                  <div className="sn-row" style={{ gap: "var(--space-1)", flex: "none" }}>
                    {d.status === "Proposed" && (
                      <>
                        <Button size="sm" icon="Check" onClick={() => decide(d.id, "Approved")} data-testid="pm-approve-btn">Approve</Button>
                        <Button size="sm" icon="X" onClick={() => decide(d.id, "Rejected")} data-testid="pm-reject-btn">Reject</Button>
                      </>
                    )}
                    <IconButton icon={Trash2} label="Delete decision" size="sm" onClick={() => del(d.id)} />
                  </div>
                )}
              </div>
              {d.detail && <p className="sn-dlog-b" style={{ whiteSpace: "pre-wrap" }}>{d.detail}</p>}
              <DThread did={d.id} />
            </article>
          ))}
        </Panel>
      )}
    </div>
  );
}
